/**
 * Full-screen annotation flow at `/v4/go-lab/annotate/queue/:recordId`.
 *
 * Sits outside `GoLabLayout`/`AnnotateLayout` so the annotator gets a
 * focused, distraction-free surface (left pane: record context; right
 * pane: step bar + label pickers + review).
 *
 * Data cascade: record → collection (for taxonomyKey) → taxonomy version.
 * Each `enabled` flag waits for the previous query to land.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '../../../ui/button';
import { useAuth } from '../../../../contexts/AuthContext';
import { useCollection } from '../datasets/hooks/useDatasets';
import { useVersionDetail } from '../hooks/useTaxonomy';
import {
  useDatasetRecord,
  useStartAnnotation,
  useSubmitAnnotation,
} from './hooks/useAnnotation';
import {
  inferAnnotationPass,
  serialiseLabels,
  useAnnotationFlow,
} from './hooks/useAnnotationFlow';
import RecordContextPane from './components/flow/RecordContextPane';
import AnnotationStepBar from './components/flow/AnnotationStepBar';
import LabelSelectorPane from './components/flow/LabelSelectorPane';
import ReviewPane from './components/flow/ReviewPane';
import AgreementModal from './components/flow/AgreementModal';
import DisagreementModal from './components/flow/DisagreementModal';
import {
  ErrorScreen,
  FlowHeaderBreadcrumb,
  LoadingScreen,
} from './components/flow/AnnotationFlowChrome';
import RecordCompleteScreen from './components/flow/RecordCompleteScreen';
import AlreadyAnnotatedScreen from './components/flow/AlreadyAnnotatedScreen';
import ClaimGate from './components/flow/ClaimGate';


/**
 * After a second-pass submit the page renders either the agreement modal
 * or the disagreement modal based on the server's `agreement` payload.
 * First-pass submits never set this state — they just toast and navigate.
 */
type PostSubmitModal =
  | { kind: 'none' }
  | { kind: 'agreement'; agreedCount: number; totalDimensions: number }
  | { kind: 'disagreement'; agreedCount: number; disputedDimensionNames: string[] };


const AnnotationFlowPage: React.FC = () => {
  const params = useParams<{ recordId: string }>();
  const recordId = params.recordId ?? '';
  const navigate = useNavigate();
  const { user } = useAuth();

  // 1. Fetch the record (and its current annotations / resolution).
  const recordQuery = useDatasetRecord(recordId);

  // 2. Resolve the taxonomy key via the record's collection.
  const collectionId = recordQuery.data?.record.collectionId;
  const collectionQuery = useCollection(collectionId ?? '');

  // 3. Fetch the pinned taxonomy version.
  const taxonomyKey = collectionQuery.data?.taxonomyKey ?? '';
  const versionId = recordQuery.data?.record.taxonomyVersionId ?? '';
  const versionQuery = useVersionDetail(taxonomyKey, versionId);

  // Local flow state.
  const [state, dispatch] = useAnnotationFlow();

  // Which post-submit modal is open (if any). Driven by the server's
  // `agreement` payload returned from a second-pass submit.
  const [modal, setModal] = useState<PostSubmitModal>({ kind: 'none' });

  // Submit mutation. The hook is curried by record id.
  const submitAnnotation = useSubmitAnnotation(recordId);
  // Explicit-id claim — used by the gate when the user isn't already the
  // assigned annotator. On success the record-detail cache is invalidated
  // and the gate re-renders with `isAssigned = true`, swapping to the flow.
  const startClaim = useStartAnnotation(recordId);

  // Derive dimensions (empty list while loading) so memoised hooks below
  // stay called every render — Rules of Hooks. Early returns happen AFTER
  // all hooks are called. Wrapped in `useMemo` so the `?? []` fallback
  // doesn't return a fresh array each render and bust downstream memo deps.
  //
  // Filter to the collection's selected dimension scope: a collection
  // typically annotates a subset of the pinned taxonomy version. Without
  // this filter the annotator would step through every dimension in
  // the taxonomy and `serialiseLabels` would push out-of-scope labels
  // to the BE, contaminating agreement / gold data. A stale dim in
  // `collection.dimensions` (no longer present in the version) is
  // silently dropped — same intersection contract as
  // `Collection.resolvedDimensions`.
  const dimensions = useMemo(() => {
    const all = versionQuery.data?.taxonomyData?.dimensions ?? [];
    const scope = collectionQuery.data?.dimensions;
    if (!scope) return [];
    const allowed = new Set(scope);
    return all.filter((d) => allowed.has(d.id));
  }, [
    versionQuery.data?.taxonomyData?.dimensions,
    collectionQuery.data?.dimensions,
  ]);
  const REVIEW_INDEX = dimensions.length;
  const isReview = state.currentStep === REVIEW_INDEX;

  const satisfiedSteps = useMemo(() => {
    const s = new Set<number>();
    dimensions.forEach((dim, idx) => {
      const ds = state.perDimension[dim.id];
      if (ds && (ds.noneApply || ds.selected.length > 0)) s.add(idx);
    });
    if (isReview) s.add(dimensions.length);
    return s;
  }, [dimensions, state.perDimension, isReview]);

  const steps = useMemo(
    () => [
      ...dimensions.map((d, idx) => ({ label: d.name, index: idx })),
      { label: 'Review', index: REVIEW_INDEX },
    ],
    [dimensions, REVIEW_INDEX],
  );

  // ----- Loading / error guards (all hooks called above) -----

  const goBackToQueue = () => navigate('/v4/go-lab/annotate/queue');

  if (
    recordQuery.isLoading ||
    (collectionId && collectionQuery.isLoading) ||
    (taxonomyKey && versionId && versionQuery.isLoading)
  ) {
    return <LoadingScreen />;
  }

  if (recordQuery.isError || !recordQuery.data) {
    return (
      <ErrorScreen
        message="We couldn't load this record."
        onBack={goBackToQueue}
      />
    );
  }

  const record = recordQuery.data.record;
  const detail = recordQuery.data;
  const userId = user?.user_id;

  // A post-submit modal is open. The second-pass submit's `onSuccess`
  // invalidated the record-detail query: once the refetch lands, the
  // current user appears as the second annotator AND `record.status`
  // flips to a terminal value, which would otherwise trigger either
  // the `myAnnotation` (`AlreadyAnnotatedScreen`) or the `pass === null`
  // (`RecordCompleteScreen`) early-return and unmount the modal subtree
  // before the user has seen the agreement / disagreement result.
  // Short-circuit here so the chrome + record context + modal stay
  // mounted until the user dismisses the modal (which navigates away).
  if (modal.kind !== 'none') {
    return (
      <div className="flex h-screen flex-col bg-[var(--go-spring)]">
        <FlowHeaderBreadcrumb recordId={record.id} />
        <div className="grid flex-1 grid-cols-1 gap-4 overflow-hidden px-4 py-4 md:grid-cols-[40%_60%] md:gap-6 md:px-6 md:py-6">
          <RecordContextPane record={record} />
        </div>
        {modal.kind === 'agreement' && (
          <AgreementModal
            open
            onOpenChange={(o) => {
              if (!o) {
                setModal({ kind: 'none' });
                goBackToQueue();
              }
            }}
            agreedCount={modal.agreedCount}
            totalDimensions={modal.totalDimensions}
            onContinue={() => {
              setModal({ kind: 'none' });
              goBackToQueue();
            }}
          />
        )}
        {modal.kind === 'disagreement' && (
          <DisagreementModal
            open
            onOpenChange={(o) => {
              if (!o) {
                setModal({ kind: 'none' });
                goBackToQueue();
              }
            }}
            agreedCount={modal.agreedCount}
            disputedDimensionNames={modal.disputedDimensionNames}
            onBackToQueue={() => {
              setModal({ kind: 'none' });
              goBackToQueue();
            }}
            onGoToReview={() => {
              setModal({ kind: 'none' });
              navigate('/v4/go-lab/annotate/review');
            }}
          />
        )}
      </div>
    );
  }

  // "Already annotated by me" guard. The detail response carries the live
  // first + second annotations; if either was authored by the current user
  // they shouldn't be routed back into the labelling UI (and the BE's
  // same-user rule would reject any re-submit anyway).
  const myFirstAnnotation =
    detail.firstAnnotation && detail.firstAnnotation.annotatorId === userId
      ? detail.firstAnnotation
      : null;
  const mySecondAnnotation =
    detail.secondAnnotation && detail.secondAnnotation.annotatorId === userId
      ? detail.secondAnnotation
      : null;
  const myAnnotation = myFirstAnnotation ?? mySecondAnnotation;

  if (myAnnotation) {
    return (
      <div className="flex h-screen flex-col bg-[var(--go-spring)]">
        <FlowHeaderBreadcrumb recordId={record.id} />
        <div className="grid flex-1 grid-cols-1 gap-4 overflow-hidden px-4 py-4 md:grid-cols-[40%_60%] md:gap-6 md:px-6 md:py-6">
          <RecordContextPane record={record} />
          <AlreadyAnnotatedScreen
            record={record}
            myAnnotation={myAnnotation}
            onBack={goBackToQueue}
          />
        </div>
      </div>
    );
  }

  const pass = inferAnnotationPass(record.status);
  if (pass === null) {
    // `inferAnnotationPass` returns null for the four terminal-ish
    // states (annotated_both / agreed / disputed / finalised). Three
    // of four are normal "no longer your job" situations — render a
    // friendly panel rather than a generic error with the raw enum.
    return (
      <RecordCompleteScreen
        status={record.status}
        onBack={goBackToQueue}
      />
    );
  }

  if (collectionQuery.isError || versionQuery.isError) {
    return (
      <ErrorScreen
        message="We couldn't load this record's taxonomy."
        onBack={goBackToQueue}
      />
    );
  }

  if (!record.taxonomyVersionId) {
    return (
      <ErrorScreen
        message="This record has no pinned taxonomy version; annotation isn't possible."
        onBack={goBackToQueue}
      />
    );
  }

  if (!versionQuery.data) {
    return <LoadingScreen />;
  }

  // ----- Gate: hide the flow until the user is the assigned annotator -----
  //
  // `isAssigned` is true when the current user already owns either the
  // first-pass slot (status pending/assigned_first/annotated_first) or
  // the second-pass slot (status assigned_second/annotated_first). Users
  // arriving via the Queue page are always assigned (queue filter
  // requires it); users arriving via "Pick up next" are also already
  // assigned (the BE claimed before navigating). The gate appears only
  // for direct URL access to an unclaimed-by-them record, or a `pending`
  // row from a future admin surface.
  const isAssigned =
    !!userId &&
    (record.firstAnnotatorId === userId ||
      record.secondAnnotatorId === userId);

  const handleClaim = async () => {
    try {
      await startClaim.mutateAsync();
      // The hook invalidates `annotationKeys.record(recordId)`. The
      // useDatasetRecord query refetches → `isAssigned` flips to true on
      // the next render → the gate disappears and the flow renders.
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to claim this record.';
      toast.error(message);
    }
  };

  // ----- Flow render -----

  const activeDimension = isReview ? null : dimensions[state.currentStep];
  const canAdvance =
    isReview ||
    (activeDimension
      ? (state.perDimension[activeDimension.id]?.selected.length ?? 0) > 0 ||
        state.perDimension[activeDimension.id]?.noneApply
      : false);

  const onSubmit = async () => {
    try {
      const result = await submitAnnotation.mutateAsync({
        annotationPass: pass,
        labels: serialiseLabels(dimensions, state.perDimension),
        confidenceNote: state.confidenceNote.trim() || null,
      });
      // First pass — server returns `agreement: null`; toast + navigate.
      if (!result.agreement) {
        toast.success('Annotation submitted — awaiting second annotator');
        navigate('/v4/go-lab/annotate/queue');
        return;
      }
      // Second pass — open the agreement or disagreement modal. Disputed
      // dimension ids arrive in server-determined order (matches the
      // record's pinned taxonomy); resolve to display names via the
      // taxonomy we've already fetched.
      const dimNameById = Object.fromEntries(
        dimensions.map((d) => [d.id, d.name]),
      );
      if (result.agreement.matched) {
        setModal({
          kind: 'agreement',
          agreedCount: result.agreement.agreedDimensions.length,
          totalDimensions: dimensions.length,
        });
      } else {
        setModal({
          kind: 'disagreement',
          agreedCount: result.agreement.agreedDimensions.length,
          disputedDimensionNames: result.agreement.disputedDimensions.map(
            (id) => dimNameById[id] ?? id,
          ),
        });
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to submit annotation.';
      toast.error(message);
    }
  };

  return (
    <div className="flex h-screen flex-col bg-[var(--go-spring)]">
      <FlowHeaderBreadcrumb recordId={record.id} />

      <div className="grid flex-1 grid-cols-1 gap-4 overflow-hidden px-4 py-4 md:grid-cols-[40%_60%] md:gap-6 md:px-6 md:py-6">
        <RecordContextPane record={record} />

        {!isAssigned ? (
          <ClaimGate
            record={record}
            isClaiming={startClaim.isPending}
            onClaim={handleClaim}
          />
        ) : (
          <div className="flex min-h-0 flex-col gap-4 overflow-y-auto rounded-2xl border bg-background p-4">
            <AnnotationStepBar
              steps={steps}
              currentStep={state.currentStep}
              visited={state.visited}
              satisfied={satisfiedSteps}
              onStepClick={(index) => dispatch({ type: 'goToStep', index })}
            />

            {isReview ? (
              <ReviewPane
                dimensions={dimensions}
                perDimension={state.perDimension}
                confidenceNote={state.confidenceNote}
                isSubmitting={submitAnnotation.isPending}
                onEditDimension={(stepIndex) =>
                  dispatch({ type: 'goToStep', index: stepIndex })
                }
                onConfidenceNoteChange={(value) =>
                  dispatch({ type: 'setConfidenceNote', value })
                }
                onBack={() =>
                  dispatch({
                    type: 'goToStep',
                    index: Math.max(0, dimensions.length - 1),
                  })
                }
                onSubmit={onSubmit}
              />
            ) : activeDimension ? (
              <>
                <LabelSelectorPane
                  dimension={activeDimension}
                  selected={state.perDimension[activeDimension.id]?.selected ?? []}
                  noneApply={
                    state.perDimension[activeDimension.id]?.noneApply ?? false
                  }
                  onToggleLabel={(labelId) =>
                    dispatch({
                      type: 'toggleLabel',
                      dimKey: activeDimension.id,
                      labelId,
                    })
                  }
                  onToggleNoneApply={() =>
                    dispatch({ type: 'toggleNoneApply', dimKey: activeDimension.id })
                  }
                />
                <div className="flex items-center justify-between border-t border-border pt-3">
                  <Button
                    variant="ghost"
                    onClick={() =>
                      dispatch({ type: 'goToStep', index: state.currentStep - 1 })
                    }
                    disabled={state.currentStep === 0}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() =>
                      dispatch({ type: 'goToStep', index: state.currentStep + 1 })
                    }
                    disabled={!canAdvance}
                    data-testid="next-step"
                  >
                    Next
                  </Button>
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>

      {/*
        Post-submit modal rendering lives in the short-circuit branch
        above so it stays mounted through the record-detail refetch.
        Reaching this point means `modal.kind === 'none'`, so no modal
        markup is needed here.
      */}
    </div>
  );
};


export default AnnotationFlowPage;
